import { Injectable } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';

/**
 * Client half of the UI <-> API payload encryption (server half:
 * BSFMS-API/src/middleware/payloadCrypto.js).
 *
 * Holds one AES-256-GCM key per session, wrapped with the API's RSA-OAEP
 * public key. The wrapped key travels in X-Enc-Key on every request; the API
 * caches the unwrapped key, so RSA work happens about once per session rather
 * than once per request. Every message still gets a fresh random IV.
 *
 * Everything lives in memory only: a reload starts a new session key.
 */

export interface PayloadCryptoSession {
    key: CryptoKey;
    wrappedKey: string;
    /** serverTime - Date.now() at key fetch, used for the request timestamp. */
    clockOffsetMs: number;
    createdAt: number;
}

export interface EncryptedEnvelope {
    enc: 1;
    z?: 1;
    data: string;
}

interface PublicKeyResponse {
    kid: string;
    publicKey: string;
    serverTime: number;
}

const IV_BYTES = 12;
// Rotate the session key periodically; the API's cache entry simply ages out.
const SESSION_TTL_MS = 60 * 60 * 1000;
// When the API has encryption off (503), is older than this feature (404) or
// keeps rejecting our key, go plain for a while instead of retrying each call.
const UNAVAILABLE_BACKOFF_MS = 5 * 60 * 1000;
const GZIP_THRESHOLD_BYTES = 1024;

@Injectable({ providedIn: 'root' })
export class PayloadCryptoService {
    /** Bypasses interceptors, avoiding a DI cycle and a request loop. */
    private readonly http: HttpClient;
    private session: Promise<PayloadCryptoSession | null> | null = null;
    private unavailableUntil = 0;

    readonly gzipSupported = typeof (window as any).CompressionStream === 'function'
        && typeof (window as any).DecompressionStream === 'function';

    constructor(httpBackend: HttpBackend) {
        this.http = new HttpClient(httpBackend);
    }

    get enabled(): boolean {
        return environment.payloadEncryption === true && !!globalThis.crypto?.subtle;
    }

    /** Current session, or null when encryption is unavailable (send plain). */
    getSession(): Promise<PayloadCryptoSession | null> {
        if (!this.enabled || Date.now() < this.unavailableUntil) {
            return Promise.resolve(null);
        }
        if (!this.session) {
            this.session = this.createSession().catch(() => {
                this.markUnavailable();
                return null;
            });
        }
        return this.session.then(s => {
            if (s && Date.now() - s.createdAt > SESSION_TTL_MS) {
                this.session = null;
                return this.getSession();
            }
            return s;
        });
    }

    /** Drop the session so the next request refetches the public key. */
    reset(): void {
        this.session = null;
    }

    /** Stop encrypting for a while (API rejected the key twice, or has it off). */
    markUnavailable(): void {
        this.session = null;
        this.unavailableUntil = Date.now() + UNAVAILABLE_BACKOFF_MS;
    }

    async encrypt(session: PayloadCryptoSession, body: unknown, aad: string): Promise<EncryptedEnvelope> {
        const json = JSON.stringify({ ts: Date.now() + session.clockOffsetMs, body });
        let plaintext: Uint8Array = new TextEncoder().encode(json);
        let compressed = false;
        if (this.gzipSupported && plaintext.length >= GZIP_THRESHOLD_BYTES) {
            plaintext = await this.transform(plaintext, new (window as any).CompressionStream('gzip'));
            compressed = true;
        }

        const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES));
        const ct = await crypto.subtle.encrypt(
            { name: 'AES-GCM', iv, additionalData: new TextEncoder().encode(aad) },
            session.key,
            plaintext
        );
        const envelope: EncryptedEnvelope = { enc: 1, data: toBase64(concat(iv, new Uint8Array(ct))) };
        if (compressed) {
            envelope.z = 1;
        }
        return envelope;
    }

    async decrypt(session: PayloadCryptoSession, envelope: EncryptedEnvelope, aad: string): Promise<unknown> {
        const data = fromBase64(envelope.data);
        let plaintext: Uint8Array = new Uint8Array(await crypto.subtle.decrypt(
            { name: 'AES-GCM', iv: data.subarray(0, IV_BYTES), additionalData: new TextEncoder().encode(aad) },
            session.key,
            data.subarray(IV_BYTES)
        ));
        if (envelope.z === 1) {
            plaintext = await this.transform(plaintext, new (window as any).DecompressionStream('gzip'));
        }
        return JSON.parse(new TextDecoder().decode(plaintext));
    }

    static isEnvelope(value: unknown): value is EncryptedEnvelope {
        return !!value && typeof value === 'object'
            && (value as any).enc === 1 && typeof (value as any).data === 'string';
    }

    private async createSession(): Promise<PayloadCryptoSession> {
        const res = await firstValueFrom(
            this.http.get<PublicKeyResponse>(`${environment.apiUrl}/crypto/public-key`)
        );
        const rsaKey = await crypto.subtle.importKey(
            'spki',
            fromBase64(res.publicKey),
            { name: 'RSA-OAEP', hash: 'SHA-256' },
            false,
            ['encrypt']
        );
        const key = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']);
        const raw = await crypto.subtle.exportKey('raw', key);
        const wrapped = await crypto.subtle.encrypt({ name: 'RSA-OAEP' }, rsaKey, raw);

        return {
            key,
            wrappedKey: toBase64(new Uint8Array(wrapped)),
            clockOffsetMs: res.serverTime - Date.now(),
            createdAt: Date.now()
        };
    }

    private async transform(input: Uint8Array, stream: TransformStream<Uint8Array, Uint8Array>): Promise<Uint8Array> {
        const out = new Blob([input]).stream().pipeThrough(stream);
        return new Uint8Array(await new Response(out).arrayBuffer());
    }
}

function concat(a: Uint8Array, b: Uint8Array): Uint8Array {
    const out = new Uint8Array(a.length + b.length);
    out.set(a, 0);
    out.set(b, a.length);
    return out;
}

function toBase64(bytes: Uint8Array): string {
    let binary = '';
    const chunk = 0x8000;
    for (let i = 0; i < bytes.length; i += chunk) {
        binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
    }
    return btoa(binary);
}

function fromBase64(b64: string): Uint8Array {
    const binary = atob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
}
